// Единый контроллер тулбара/режимов под доской — общий для разбора, зевков и
// дебютов. Owner-модель поведения (одна на всех экранах):
//   ⏮◀▶⏭ — навигация ТЕКУЩЕГО контекста: в игре с ботом (🤖 active) это откат/redo
//           по ленте партии (play.*), иначе — навигация экрана (host.*).
//   💡 — persistent-тумблер «лучший ход» (localStorage, переживает экраны):
//           в игре — on-demand стрелка движка (play.setShowBest), иначе — стрелка
//           источника экрана (host.applyBest: артефакты/теория/пазл).
//   🤖 — тумблер игры с движком; можно включить/остановить/снова включить.
// 💡 и 🤖 НЕЗАВИСИМЫ и АДДИТИВНЫ: играешь с ботом и одновременно видишь стрелку.
import { haptic } from '../tg';
import { createGameNav, type GameNav } from './game-nav';
import { loadShowBest, saveShowBest } from '../services/toolbar-state';
import type { PlayEngineController } from './play-engine';

/** Экран-хост даёт колбэки навигации/стрелки/старта игры; тумблеры держит тулбар. */
export interface BoardHost {
  first(): void;
  prev(): void;
  next(): void;
  last(): void;
  /** Состояние навигации экрана (для ⏮◀▶⏭ вне игры). */
  navState(): { atStart: boolean; atEnd: boolean };
  flip(): void;
  /** Показать/снять стрелку лучшего хода источника экрана (артефакты/теория/пазл). */
  applyBest(show: boolean): void;
  /** Начать партию с ботом из контекста экрана (сам вызывает play.start). */
  startPlay(): void;
  /** Контроллер игры с движком — для роутинга навигации/тумблеров и takeback. */
  play: PlayEngineController;
}

export class BoardToolbar {
  private nav: GameNav;
  private showBest: boolean;

  constructor(private host: BoardHost) {
    this.showBest = loadShowBest();
    this.nav = createGameNav({
      onFirst: () => this.route(() => this.host.first(), () => this.host.play.toStart()),
      onPrev: () => this.route(() => this.host.prev(), () => this.host.play.takeBack()),
      onNext: () => this.route(() => this.host.next(), () => this.host.play.forward()),
      onLast: () => this.route(() => this.host.last(), () => this.host.play.toLatest()),
      onFlip: () => {
        haptic();
        this.host.flip();
      },
      onBest: () => this.onBest(),
      onPlay: () => this.onPlay(),
    });
    // Игра сообщает об изменениях (ход бота/финиш/навигация) — обновляем ⏮◀▶⏭.
    this.host.play.hookChange(() => this.refresh());
    this.nav.setBest(this.showBest);
    this.host.applyBest(this.showBest); // стартовая стрелка источника экрана
    this.refresh();
  }

  /** DOM тулбара (хост монтирует через replaceWith на плейсхолдере [data-nav]). */
  get el(): HTMLElement {
    return this.nav.el;
  }

  /** Текущее состояние 💡 (хост может учитывать при первом рендере стрелки). */
  get bestOn(): boolean {
    return this.showBest;
  }

  private route(hostFn: () => void, playFn: () => void): void {
    haptic();
    if (this.host.play.active) playFn();
    else hostFn();
    this.refresh();
  }

  private onBest(): void {
    haptic();
    this.showBest = !this.showBest;
    saveShowBest(this.showBest);
    this.nav.setBest(this.showBest);
    if (this.host.play.active) this.host.play.setShowBest(this.showBest);
    else this.host.applyBest(this.showBest);
  }

  private onPlay(): void {
    haptic('medium');
    if (this.host.play.active) {
      this.host.play.stop(); // тумблер: отжали — бот перестал ходить (onExit вернёт экран)
      return;
    }
    this.host.startPlay();
    this.host.play.setShowBest(this.showBest); // 💡 переносится в игру (аддитивно)
    this.refresh();
  }

  /** Хост вызывает после выхода из игры (play.onExit): вернуть стрелку экрана. */
  onPlayExit(): void {
    this.host.applyBest(this.showBest);
    this.refresh();
  }

  /** Хост вызывает после собственной навигации; тулбар — после откатов игры. */
  refresh(): void {
    const active = this.host.play.active;
    const s = active ? this.host.play.navState() : this.host.navState();
    this.nav.setNav(s.atStart, s.atEnd);
    this.nav.setPlay(active);
  }

  setHintBusy(busy: boolean): void {
    this.nav.setHintBusy(busy);
  }
}
